import * as React from "react"

import { activeFilterCount, activeFilters, type FilterValues } from "./filter-types"

/*
   The one hook every table screen mounts. Owns pagination, sort, filters,
   search, and delegates fetching to an injected `fetchData`.

   fetchData is where the transport lives — a React Query hook, direct
   fetch, or an in-memory query over already-loaded rows. The params shape
   is the API contract; callers stay identical regardless of the source.
*/

export interface TableSort {
  field: string
  dir: "asc" | "desc"
}

export interface TableQueryParams {
  page: number
  pageSize: number
  sort: TableSort | null
  filters: FilterValues
  search: string
}

export interface TableFetchResult<TRow> {
  rows: TRow[]
  total: number
}

export interface UseTableOptions<TRow> {
  fetchData: (params: TableQueryParams) => Promise<TableFetchResult<TRow>> | TableFetchResult<TRow>
  defaultPageSize?: number
  defaultSort?: TableSort | null
  /** Persist state under this key. Omit to disable persistence. */
  storageKey?: string
  /** Milliseconds. Search value is debounced before it triggers a fetch. */
  searchDebounceMs?: number
}

export interface TableState<TRow> {
  rows: TRow[]
  total: number
  isLoading: boolean
  error: Error | null
  page: number
  pageSize: number
  sort: TableSort | null
  filters: FilterValues
  search: string
  /** Number of active filter axes — for the "Filters (n)" badge. */
  filterCount: number
}

export interface TableActions {
  setPage: (page: number) => void
  setPageSize: (size: number) => void
  setSort: (sort: TableSort | null) => void
  setFilter: (id: string, value: FilterValues[string]) => void
  setFilters: (values: FilterValues) => void
  clearFilter: (id: string) => void
  clearAllFilters: () => void
  setSearch: (search: string) => void
  refresh: () => void
}

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_DEBOUNCE_MS = 200

export function useTable<TRow>({
  fetchData,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  defaultSort = null,
  storageKey,
  searchDebounceMs = DEFAULT_DEBOUNCE_MS,
}: UseTableOptions<TRow>): { state: TableState<TRow>; actions: TableActions } {
  const persisted = React.useMemo(() => loadPersisted(storageKey), [storageKey])

  const [page, setPage] = React.useState<number>(persisted?.page ?? 1)
  const [pageSize, setPageSize] = React.useState<number>(persisted?.pageSize ?? defaultPageSize)
  const [sort, setSort] = React.useState<TableSort | null>(persisted?.sort ?? defaultSort)
  const [filters, setFiltersState] = React.useState<FilterValues>(persisted?.filters ?? {})
  const [search, setSearch] = React.useState<string>("")

  const [rows, setRows] = React.useState<TRow[]>([])
  const [total, setTotal] = React.useState<number>(0)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState<Error | null>(null)

  const debouncedSearch = useDebounced(search, searchDebounceMs)

  // Any filter, search, or page-size change re-anchors to page 1.
  const prevKey = React.useRef<string>("")
  React.useEffect(() => {
    const key = JSON.stringify([filters, debouncedSearch, pageSize])
    if (prevKey.current && prevKey.current !== key) {
      setPage(1)
    }
    prevKey.current = key
  }, [filters, debouncedSearch, pageSize])

  // Persist non-ephemeral state (excluding search, which is transient input).
  React.useEffect(() => {
    if (!storageKey) return
    savePersisted(storageKey, { page, pageSize, sort, filters })
  }, [storageKey, page, pageSize, sort, filters])

  // Fetch. Debounced search means each keystroke does not stampede.
  // `refreshTick` bumps to force a re-fetch when nothing else changed (e.g. after a mutation).
  const requestId = React.useRef(0)
  const [refreshTick, setRefreshTick] = React.useState(0)
  React.useEffect(() => {
    const id = ++requestId.current
    setIsLoading(true)
    setError(null)

    const params: TableQueryParams = {
      page,
      pageSize,
      sort,
      filters: activeFilters(filters),
      search: debouncedSearch,
    }

    Promise.resolve(fetchData(params))
      .then((result) => {
        // Ignore stale responses — the user changed the query while this was in flight.
        if (id !== requestId.current) return
        setRows(result.rows)
        setTotal(result.total)
        setIsLoading(false)
      })
      .catch((e: unknown) => {
        if (id !== requestId.current) return
        setError(e instanceof Error ? e : new Error(String(e)))
        setIsLoading(false)
      })
  }, [fetchData, page, pageSize, sort, filters, debouncedSearch, refreshTick])

  const actions = React.useMemo<TableActions>(
    () => ({
      setPage,
      setPageSize,
      setSort,
      setFilter: (id, value) => {
        setFiltersState((prev) => ({ ...prev, [id]: value }))
      },
      setFilters: (values) => {
        setFiltersState(values)
      },
      clearFilter: (id) => {
        setFiltersState((prev) => {
          const next = { ...prev }
          delete next[id]
          return next
        })
      },
      clearAllFilters: () => {
        setFiltersState({})
      },
      setSearch,
      refresh: () => {
        requestId.current++ // invalidate in-flight
        setRefreshTick((t) => t + 1) // triggers the fetch effect
      },
    }),
    [],
  )

  const state: TableState<TRow> = {
    rows,
    total,
    isLoading,
    error,
    page,
    pageSize,
    sort,
    filters,
    search,
    filterCount: activeFilterCount(filters),
  }

  return { state, actions }
}

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value)
  React.useEffect(() => {
    if (delay <= 0) {
      setDebounced(value)
      return
    }
    const t = window.setTimeout(() => {
      setDebounced(value)
    }, delay)
    return () => {
      window.clearTimeout(t)
    }
  }, [value, delay])
  return debounced
}

interface PersistedTableState {
  page: number
  pageSize: number
  sort: TableSort | null
  filters: FilterValues
}

function storageKeyFor(id: string): string {
  return `pyxis:table:${id}`
}

function loadPersisted(id: string | undefined): PersistedTableState | null {
  if (!id || typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(storageKeyFor(id))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedTableState
    return parsed
  } catch {
    return null
  }
}

function savePersisted(id: string, state: PersistedTableState): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(storageKeyFor(id), JSON.stringify(state))
  } catch {
    /* quota or private-mode — silently skip */
  }
}
